import { PageStage } from "src/shared/contexts/HackerContext/types";
import { DiscoveryProfile } from "src/shared/contexts/ProfileContext";
import { Nullable } from "src/shared/utils/typescript";
import { ProfileFormValues } from "src/views/profile/components/ProfileForm/types";
import { buildProfileFormValues as buildBaseProfileFormValues } from "src/views/profile/components/ProfileForm/values";

export type PrefillSources = {
  /** Application preferred name (`rsvp_preferred_name`). */
  preferredName?: Nullable<string>;
  /** Personal RSVP preferred pronouns (`rsvp_preferred_pronouns`). */
  pronouns?: Nullable<string[]>;
  /** Saved contact values from the Your Badge RSVP step. */
  badgeContacts?: Partial<
    Record<
      | "contact_email"
      | "contact_phone"
      | "contact_linkedin"
      | "contact_instagram"
      | "contact_discord"
      | "contact_x",
      Nullable<string>
    >
  >;
};

export type InitialDirectoryProfileSeed = Partial<
  Pick<
    ProfileFormValues,
    "name" | "pronouns" | "contact_linkedin" | "contact_discord" | "contact_x"
  >
>;

const CONTACT_FIELDS = [
  "contact_linkedin",
  "contact_discord",
  "contact_x",
] as const;

const hasText = (value: Nullable<string> | undefined): value is string =>
  value != null && value.trim() !== "";

/**
 * First RSVP pass only: fill an empty discovery name from preferred name.
 * Once any discovery answer exists, preserve claim values exactly so fields
 * deliberately cleared by the hacker remain cleared.
 */
export const buildProfileFormValues = (
  profile: DiscoveryProfile | null,
  sources: PrefillSources
): ProfileFormValues => {
  const values = buildBaseProfileFormValues(profile);
  // Claim metadata and the normalized photo container are not answers used to
  // decide whether this is the hacker's first RSVP pass.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { claimId, isPublished, profile_photo, ...answerFields } =
    profile ?? {};
  const hasDiscoveryAnswers = Object.values(answerFields).some((value) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return true;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });

  if (hasDiscoveryAnswers) return values;

  const pronouns = (sources.pronouns ?? [])
    .map((pronoun) => pronoun.trim())
    .filter(Boolean);

  return {
    ...values,
    name:
      values.name == null || values.name.trim() === ""
        ? sources.preferredName?.trim() || null
        : values.name,
    pronouns:
      values.pronouns == null || values.pronouns.length === 0
        ? pronouns.length > 0
          ? pronouns
          : null
        : values.pronouns,
  };
};

/** Only the first successful Badge completion initializes Directory fields. */
export const shouldInitializeDirectoryProfile = (
  badgeStage: Nullable<string> | undefined
): boolean => badgeStage !== PageStage.COMPLETED;

/**
 * Build the partial Directory draft written on first Badge completion. Existing
 * Directory values are omitted so the one-time copy never overwrites them.
 */
export const buildInitialDirectoryProfileSeed = (
  profile: DiscoveryProfile | null,
  sources: PrefillSources,
  badgeStage: Nullable<string> | undefined
): InitialDirectoryProfileSeed => {
  if (!shouldInitializeDirectoryProfile(badgeStage) || profile?.isPublished) {
    return {};
  }

  const savedValues = buildBaseProfileFormValues(profile);
  const initialValues = buildProfileFormValues(profile, sources);
  const seed: InitialDirectoryProfileSeed = {};

  if (initialValues.name !== savedValues.name && hasText(initialValues.name)) {
    seed.name = initialValues.name;
  }
  if (initialValues.pronouns !== savedValues.pronouns) {
    seed.pronouns = initialValues.pronouns;
  }

  CONTACT_FIELDS.forEach((field) => {
    const badgeValue = sources.badgeContacts?.[field];
    if (!hasText(savedValues[field]) && hasText(badgeValue)) {
      seed[field] = badgeValue.trim();
    }
  });

  return seed;
};
